// Phase 4.2 (hh-2jb) — WalkthroughScreen.
//
// Ported from designproject/files/screens-onboard.jsx (`WalkthroughScreen`).
// case-44: серия вопросов «есть ли у вас X?» с безопасным прерыванием.
// Прогресс персистится в OnboardingSession (POST/PATCH /api/v1/onboarding):
// при возврате экран восстанавливается с того же вопроса. Ответ «есть» создаёт
// реальную карточку оборудования (POST /api/v1/equipment) и заводит дефолтный
// регламент ТО из каталога (POST .../schedule на каждое правило) — поэтому
// результат прохождения это настоящие eq + непустой календарь ТО, а не мок.

import type { JSX } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { T, Icon, Btn, Avatar, type IconName } from '../../tokens';
import { ScreenHeader } from '../../shell';
import type { RouterApi } from '../../router';
import { catalog, onboarding, equipment, properties, schedule, humanizeError } from '../../api';
import type { OnboardingProgress } from '../../api/endpoints/onboarding.js';
import type { EquipmentNode } from '../../api/endpoints/properties.js';
import type { CatalogEntry } from '../../api/endpoints/catalog.js';

export interface WalkthroughScreenProps {
  readonly router: RouterApi;
}

type Answer = 'yes' | 'no' | 'skip';

interface Question {
  readonly id: string;
  readonly icon: IconName;
  readonly text: string;
  /** Тип каталога — ответ «есть» создаёт оборудование этого вида. */
  readonly catalogName?: string;
  readonly catalogCategory?: string;
  /**
   * Идентификатор записи каталога (EquipmentCatalog.Type). По нему после
   * создания оборудования подтягивается дефолтный регламент ТО. Категории
   * самой по себе недостаточно — `appliance` делят стиралка и плита.
   */
  readonly catalogType?: string;
}

const QUESTIONS: readonly Question[] = [
  { id: 'q1', icon: 'drop', text: 'Есть ли у вас фильтр воды под раковиной?', catalogName: 'Фильтр для воды', catalogCategory: 'water_filter', catalogType: 'filter' },
  { id: 'q2', icon: 'flame', text: 'Газовый котёл или электрический бойлер?', catalogName: 'Газовый котёл', catalogCategory: 'boiler', catalogType: 'boiler' },
  { id: 'q3', icon: 'snow', text: 'Кондиционер?', catalogName: 'Кондиционер', catalogCategory: 'aircon', catalogType: 'ac' },
  { id: 'q4', icon: 'shield', text: 'Огнетушитель?', catalogName: 'Огнетушитель', catalogCategory: 'safety', catalogType: 'extinguisher' },
  { id: 'q5', icon: 'drop', text: 'Стиральная машина?', catalogName: 'Стиральная машина', catalogCategory: 'appliance', catalogType: 'wash' },
  { id: 'q6', icon: 'flame', text: 'Газовая плита?', catalogName: 'Газовая плита', catalogCategory: 'appliance', catalogType: 'stove' },
  { id: 'q7', icon: 'box', text: 'Что-то ещё, что обслуживается? (сейф, насос, …)' },
];

type Status = 'loading' | 'active' | 'done' | 'error';

function describeError(err: unknown): string {
  return humanizeError(err, 'generic');
}

/** Russian plural: 1 карточку · 2 карточки · 5 карточек (hh-cvy.5). */
function pluralRu(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}

export function WalkthroughScreen({ router }: WalkthroughScreenProps): JSX.Element {
  const [status, setStatus] = useState<Status>('loading');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [doneEquipment, setDoneEquipment] = useState<readonly EquipmentNode[]>([]);
  // Объект, к которому привязывается создаваемое в walkthrough оборудование.
  const [targetPropertyId, setTargetPropertyId] = useState<string | null>(null);
  // Справочник регламентов ТО — по нему ответ «есть» заводит правила.
  const [catalogEntries, setCatalogEntries] = useState<readonly CatalogEntry[]>([]);

  const total = QUESTIONS.length;

  // Резолвим целевой объект и восстанавливаем прерванную сессию либо начинаем
  // новую (case-44 — возврат с того же места).
  useEffect(() => {
    let cancelled = false;
    void (async (): Promise<void> => {
      try {
        // Equipment.Add требует существующий объект — берём первый, либо
        // создаём «Мой дом», если у пользователя ещё ничего нет. Каталог
        // тянем сразу: без него ответ «есть» не сможет завести регламент ТО.
        const [tree, cat] = await Promise.all([
          properties.getPropertyTree(),
          catalog.getEquipmentCatalog(),
        ]);
        if (cancelled) return;
        setCatalogEntries(cat.equipment);
        const firstProperty = tree.properties[0];
        const propertyId =
          firstProperty !== undefined
            ? firstProperty.id
            : (await properties.createProperty({ name: 'Мой дом' })).id;
        if (cancelled) return;
        setTargetPropertyId(propertyId);

        const res = await onboarding.getCurrentOnboarding('walkthrough');
        if (cancelled) return;
        if (res.session !== null) {
          const p = res.session.progress;
          setSessionId(res.session.id);
          setAnswers(readAnswers(p));
          setIdx(readCurrent(p, total));
        } else {
          const fresh = await onboarding.startOnboarding('walkthrough', { answers: {}, current: 0 });
          if (cancelled) return;
          setSessionId(fresh.id);
        }
        setStatus('active');
      } catch (err: unknown) {
        if (cancelled) return;
        setError(describeError(err));
        setStatus('error');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [total]);

  const finishSession = async (finalAnswers: Record<string, Answer>): Promise<void> => {
    if (sessionId !== null) {
      try {
        await onboarding.completeOnboarding(sessionId);
      } catch {
        // Завершение — не блокер для показа результата.
      }
    }
    try {
      const tree = await properties.getPropertyTree();
      const all = tree.properties.flatMap((prop) => [
        ...prop.equipment,
        ...prop.rooms.flatMap((r) => r.equipment),
      ]);
      setDoneEquipment(all);
    } catch {
      setDoneEquipment([]);
    }
    void finalAnswers;
    setStatus('done');
  };

  const answer = (a: Answer): void => {
    if (busy) return;
    const q = QUESTIONS[idx];
    if (q === undefined) return;

    setBusy(true);
    setError(null);
    const nextAnswers = { ...answers, [q.id]: a };
    const nextIdx = idx + 1;
    setAnswers(nextAnswers);

    const tasks: Promise<unknown>[] = [];
    // Ответ «есть» → создаём настоящую карточку оборудования в целевом объекте
    // и сразу заводим дефолтный регламент ТО из каталога — иначе календарь
    // остаётся пустым (hh-0gk). Правила пишутся последовательно, чтобы порядок
    // ChangeRecord был детерминированным (как в CatalogEquipmentScreen).
    if (
      a === 'yes' &&
      q.catalogName !== undefined &&
      q.catalogCategory !== undefined &&
      targetPropertyId !== null
    ) {
      const catalogName = q.catalogName;
      const catalogCategory = q.catalogCategory;
      const propertyId = targetPropertyId;
      const entry = catalogEntries.find((e) => e.type === q.catalogType) ?? null;
      tasks.push(
        equipment
          .addEquipment({ name: catalogName, category: catalogCategory, propertyId })
          .then(async (created) => {
            if (entry === null) return;
            for (const rule of entry.rules) {
              await schedule.addScheduleRule(created.id, {
                title: rule.title,
                periodMonths: rule.periodMonths,
                isSafetyCritical: rule.isSafetyCritical,
                fromCatalog: true,
              });
            }
          }),
      );
    }
    if (sessionId !== null) {
      const progress: OnboardingProgress = { answers: nextAnswers, current: nextIdx };
      tasks.push(onboarding.patchOnboarding(sessionId, progress));
    }

    Promise.all(tasks)
      .then(async () => {
        if (nextIdx >= total) {
          await finishSession(nextAnswers);
        } else {
          setIdx(nextIdx);
        }
      })
      .catch((err: unknown) => {
        setError(describeError(err));
      })
      .finally(() => {
        setBusy(false);
      });
  };

  if (status === 'loading') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: T.bg }}>
        <ScreenHeader title="Проход по дому" onBack={() => router.back()} />
        <div style={{ padding: '14px 20px', fontSize: 13, color: T.ink3 }}>Загружаю…</div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: T.bg }}>
        <ScreenHeader title="Проход по дому" onBack={() => router.back()} />
        <div style={{ padding: '14px 20px' }}>
          <ErrorBanner message={error ?? 'Не удалось загрузить онбординг.'} />
        </div>
      </div>
    );
  }

  if (status === 'done') {
    // Считаем только «да» по вопросам с типом каталога — q7 («что-то ещё»)
    // карточку не создаёт.
    const created = QUESTIONS.filter(
      (q) => q.catalogName !== undefined && answers[q.id] === 'yes',
    ).length;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: T.bg }}>
        <ScreenHeader
          title="Готово 🎉"
          subtitle={`Создал ${created} ${pluralRu(created, 'карточку', 'карточки', 'карточек')} оборудования`}
        />
        <div style={{ flex: 1, overflow: 'auto', padding: '4px 16px 24px' }}>
          <div
            style={{
              background: T.brandSoft,
              border: `1px solid ${T.brandSoft2}`,
              borderRadius: 16,
              padding: 16,
            }}
          >
            <div style={{ fontSize: 14, fontWeight: 700, color: T.brandDeep }}>Календарь ТО собран</div>
            <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 4 }}>
              Я буду напоминать, когда подойдёт срок. Safety-критичные пункты помечены.
            </div>
          </div>

          {doneEquipment.length > 0 && (
            <>
              <div
                style={{
                  fontSize: 11.5,
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  textTransform: 'uppercase',
                  color: T.ink3,
                  padding: '14px 2px 6px',
                }}
              >
                Оборудование
              </div>
              {doneEquipment.map((eq) => (
                <div
                  key={eq.id}
                  onClick={() => router.go('eq_card', { id: eq.id })}
                  style={{
                    background: T.surface,
                    border: `1px solid ${T.border}`,
                    borderRadius: 12,
                    padding: 12,
                    marginTop: 8,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    cursor: 'pointer',
                  }}
                >
                  <Avatar kind="ai" icon="boiler" size={36} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 650, color: T.ink }}>{eq.name}</div>
                    <div style={{ fontSize: 12, color: T.ink3, marginTop: 2 }}>{eq.category}</div>
                  </div>
                  <Icon name="chevR" size={16} color={T.ink3} />
                </div>
              ))}
            </>
          )}

          <Btn
            kind="primary"
            size="lg"
            style={{ width: '100%', marginTop: 20 }}
            onClick={() => router.resetTo('calendar')}
          >
            Открыть календарь
          </Btn>
        </div>
      </div>
    );
  }

  // status === 'active'
  const q = QUESTIONS[idx];
  if (q === undefined) return <div />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: T.bg }}>
      <ScreenHeader
        title="Проход по дому"
        subtitle={`Вопрос ${idx + 1} из ${total} · можно прервать в любой момент`}
        onBack={() => router.back()}
      />
      <div style={{ padding: '0 16px 12px' }}>
        <div style={{ height: 4, background: T.borderStrong, borderRadius: 2, overflow: 'hidden' }}>
          <div
            style={{
              width: `${((idx + 1) / total) * 100}%`,
              height: '100%',
              background: T.brand,
              transition: 'width 0.3s',
            }}
          />
        </div>
      </div>

      <div style={{ flex: 1, padding: '24px', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: 18,
              background: T.brandSoft,
              border: `1px solid ${T.brandSoft2}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Icon name={q.icon} size={32} color={T.brandDeep} />
          </div>
          <div
            style={{
              fontSize: 22,
              fontWeight: 700,
              letterSpacing: -0.4,
              color: T.ink,
              marginTop: 20,
              lineHeight: 1.25,
            }}
          >
            {q.text}
          </div>
          <div style={{ fontSize: 13.5, color: T.ink3, marginTop: 10 }}>
            {q.catalogName !== undefined
              ? 'На «есть» создам карточку и регламент ТО'
              : 'Остальное добавишь вручную из дерева'}
          </div>
          {error !== null && (
            <div style={{ marginTop: 14, alignSelf: 'stretch' }}>
              <ErrorBanner message={error} />
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, opacity: busy ? 0.6 : 1 }}>
          <Btn kind="primary" size="lg" icon="check" disabled={busy} onClick={() => answer('yes')}>
            Есть
          </Btn>
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn kind="outline" disabled={busy} onClick={() => answer('no')} style={{ flex: 1 }}>
              Нет
            </Btn>
            <Btn kind="ghost" disabled={busy} onClick={() => answer('skip')} style={{ flex: 1 }}>
              Не знаю
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

function readAnswers(p: OnboardingProgress): Record<string, Answer> {
  const raw = p['answers'];
  if (raw === null || typeof raw !== 'object') return {};
  const out: Record<string, Answer> = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (v === 'yes' || v === 'no' || v === 'skip') out[k] = v;
  }
  return out;
}

function readCurrent(p: OnboardingProgress, total: number): number {
  const raw = p['current'];
  if (typeof raw === 'number' && raw >= 0 && raw <= total) return raw;
  return 0;
}

function ErrorBanner({ message }: { readonly message: string }): JSX.Element {
  return (
    <div
      style={{
        padding: '10px 12px',
        background: T.dangerSoft,
        border: '1px solid #E9C7BF',
        borderRadius: 12,
        fontSize: 12.5,
        color: T.danger,
        fontWeight: 600,
      }}
    >
      {message}
    </div>
  );
}
